import type { QueryClient } from '@tanstack/react-query';
import { getSubscription } from '@/api/usage';
import { getClient } from '@/api/client';
import type { ApiSubscription } from '@/types/usage';
import type { Client } from '@/types/client';

type RefreshSubscriptionQueriesOptions = {
  expectedPlanId?: string;
  pollDelayMs?: number;
  pollTimeoutMs?: number;
};

const sleep = (delayMs: number) => new Promise(resolve => setTimeout(resolve, delayMs));

export async function refreshSubscriptionQueries(
  queryClient: QueryClient,
  clientId: string,
  options: RefreshSubscriptionQueriesOptions = {}
) {
  const { expectedPlanId, pollDelayMs = 1000, pollTimeoutMs = 15000 } = options;
  const subscriptionQueryKeys = [
    ['settings-plans-subscription', clientId],
    ['settings-usage-subscription', clientId],
    ['sidebar-subscription', clientId],
    ['home-subscription', clientId],
  ];

  const fetchLatestSubscription = () =>
    queryClient.fetchQuery({
      queryKey: ['settings-plans-subscription', clientId],
      queryFn: () => getSubscription(clientId),
      staleTime: 0,
    });

  const startedAt = Date.now();
  let subscription: ApiSubscription | undefined;
  let lastError: unknown;

  while (Date.now() - startedAt < pollTimeoutMs) {
    try {
      subscription = await fetchLatestSubscription();
      if (!expectedPlanId || subscription.plan.id === expectedPlanId) {
        break;
      }
    } catch (error) {
      lastError = error;
    }

    await sleep(pollDelayMs);
  }

  if (!subscription) {
    throw lastError;
  }

  subscriptionQueryKeys.forEach(queryKey => {
    queryClient.setQueryData(queryKey, subscription);
  });
}

type PollClientEngagementConfigOptions = {
  pollDelayMs?: number;
  pollTimeoutMs?: number;
};

/**
 * Polls GET /api/client/:clientId until engagement_config_status reaches the
 * paid-plan setup state, then updates the ['client', clientId] query cache.
 * The home page's useEffect watches this key and calls setClient() to propagate
 * the new status into UserContext.
 */
export async function pollClientEngagementConfig(
  queryClient: QueryClient,
  clientId: string,
  options: PollClientEngagementConfigOptions = {}
): Promise<Client> {
  const { pollDelayMs = 1000, pollTimeoutMs = 15000 } = options;

  const fetchLatestClient = () =>
    queryClient.fetchQuery({
      queryKey: ['client', clientId],
      queryFn: () => getClient(clientId),
      staleTime: 0,
    });

  const startedAt = Date.now();
  let client: Client | undefined;
  let lastError: unknown;

  while (Date.now() - startedAt < pollTimeoutMs) {
    try {
      client = await fetchLatestClient();
      if (client.engagement_config_status === 'pending') {
        break;
      }
    } catch (error) {
      lastError = error;
    }

    await sleep(pollDelayMs);
  }

  if (!client) {
    throw lastError;
  }

  // fetchQuery already updates the cache, but set it explicitly to be safe
  queryClient.setQueryData(['client', clientId], client);
  return client;
}
